// Feb 2026 · Simple in-app page that fetches
// /api/viatris/batch-errors-html with the user's session cookie
// attached (top-level URL-bar navigation drops the cookie in
// Emergent's ingress). Shows the HTML inline. Non-technical operator
// route to see the last N batch failure messages without DevTools.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BatchErrorsPage() {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchErrors = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/api/viatris/batch-errors-html?limit=200`, {
        withCredentials: true,
        responseType: 'text',
      });
      setHtml(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchErrors(); }, []);

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="batch-errors-page">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-rose-600" />
              Batch Errors
            </h1>
            <div className="text-sm text-slate-500 mt-1">
              Latest 200 failed rows from viatris_assessment_jobs — grouped by identical error so systemic failures are obvious.
            </div>
          </div>
          <button
            onClick={fetchErrors}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded hover:bg-slate-50"
            disabled={loading}
            data-testid="batch-errors-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {loading && (
          <div className="bg-white rounded-lg border border-slate-200 p-10 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading errors…
          </div>
        )}

        {err && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-rose-800 text-sm" data-testid="batch-errors-error">
            {err}
          </div>
        )}

        {!loading && !err && html && (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <iframe
              srcDoc={html}
              title="Batch errors"
              className="w-full min-h-[70vh] border-0"
              data-testid="batch-errors-frame"
            />
          </div>
        )}
      </div>
    </div>
  );
}
